import { useEffect, useState } from 'react'
import { Button } from '@journ/ui/button'
import { Icons } from '@journ/ui/icons'
import { Input } from '@journ/ui/input'
import { Label } from '@journ/ui/label'
import { ScrollArea } from '@journ/ui/scroll-area'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@journ/ui/select'
import { Sheet, SheetContent, SheetHeader } from '@journ/ui/sheet'
import { SubmitButton } from '@journ/ui/submit-button'
import { Switch } from '@journ/ui/switch'
import { Textarea } from '@journ/ui/textarea'
import { useIntlayer } from 'react-intlayer'
import { useSelector } from '@/libs/state'
import type { CompanyWorkflow, CreateWorkItemInput, WorkItemPriority, WorkItemSize } from '../domain/workflow'
import { creatingWorkItem$, workItems$ } from '../store'
import { useCreateWorkItem } from '../hooks/use-create-work-item'
import { useCustomerLookup } from '../hooks/use-customer-lookup'

const NO_CUSTOMER = '__none__'
const NO_PRIORITY = '__none__'
const NO_SIZE = '__none__'
const NO_PARENT = '__none__'
const PRIORITIES: WorkItemPriority[] = ['P0', 'P1', 'P2', 'P3', 'P4']
const SIZES: WorkItemSize[] = ['XS', 'S', 'M', 'L', 'XL']

const parseLabels = (raw: string): string[] =>
	raw
		.split(',')
		.map(label => label.trim())
		.filter(Boolean)

type Props = {
	workflow: CompanyWorkflow
}

const WorkItemCreateSheet = ({ workflow }: Props) => {
	const isOpen = useSelector(creatingWorkItem$)
	const workItems = useSelector(workItems$)
	const c = useIntlayer('work-item').create

	const { create } = useCreateWorkItem()
	const { customers } = useCustomerLookup(isOpen)

	const topLevelTasks = workItems.filter(w => !w.parentId && w.companyWorkflowId === workflow.id)
	const phases = [...workflow.phases].sort((a, b) => a.order - b.order)

	const [title, setTitle] = useState('')
	const [subjectName, setSubjectName] = useState('')
	const [phaseId, setPhaseId] = useState('')
	const [parentId, setParentId] = useState<string>(NO_PARENT)
	const [lightweight, setLightweight] = useState(false)
	const [externalId, setExternalId] = useState('')
	const [customerId, setCustomerId] = useState<string>(NO_CUSTOMER)
	const [priority, setPriority] = useState<string>(NO_PRIORITY)
	const [size, setSize] = useState<string>(NO_SIZE)
	const [labels, setLabels] = useState('')
	const [dueDate, setDueDate] = useState('')
	const [notes, setNotes] = useState('')
	const [submitting, setSubmitting] = useState(false)

	useEffect(() => {
		if (!isOpen) {
			setTitle('')
			setSubjectName('')
			setPhaseId('')
			setParentId(NO_PARENT)
			setLightweight(false)
			setExternalId('')
			setCustomerId(NO_CUSTOMER)
			setPriority(NO_PRIORITY)
			setSize(NO_SIZE)
			setLabels('')
			setDueDate('')
			setNotes('')
		}
	}, [isOpen])

	const close = () => creatingWorkItem$.set(false)

	const selectedPhase = phases.find(p => p.id === phaseId)
	const inboxStep = selectedPhase?.teamWorkflow.steps.find(s => s.isInbox) ?? selectedPhase?.teamWorkflow.steps[0]

	const canSubmit = Boolean(title.trim() && subjectName.trim() && selectedPhase && inboxStep)

	const submit = async () => {
		if (!canSubmit || !selectedPhase || !inboxStep) return

		const customer = customerId === NO_CUSTOMER ? null : (customers.find(cust => cust.id === customerId) ?? null)
		const parent = parentId === NO_PARENT ? undefined : parentId

		const input: CreateWorkItemInput = {
			companyWorkflowId: workflow.id,
			phaseId: selectedPhase.id,
			firstStepId: inboxStep.id,
			title: title.trim(),
			subjectName: subjectName.trim(),
			externalId: externalId.trim() || null,
			customerId: customer?.id ?? null,
			customerName: customer?.name ?? null,
			priority: priority === NO_PRIORITY ? null : (priority as WorkItemPriority),
			size: size === NO_SIZE ? null : (size as WorkItemSize),
			labels: parseLabels(labels),
			assignedTo: null,
			dueDate: dueDate || null,
			notes: notes.trim() || null,
			...(parent ? { parentId: parent, spawnMode: 'blocking' as const, lightweight } : {}),
		}

		setSubmitting(true)
		const created = await create(input)
		setSubmitting(false)
		if (created) close()
	}

	const handleSubmit = async (e: React.SubmitEvent<HTMLFormElement>) => {
		e.preventDefault()
		await submit()
	}

	return (
		<Sheet open={isOpen} onOpenChange={close}>
			<SheetContent stack title={c.title.value}>
				<SheetHeader className='mb-6 flex justify-between items-center flex-row'>
					<h2 className='text-xl'>{c.title}</h2>
					<Button size='icon' variant='ghost' onClick={close} className='p-0 m-0 size-auto hover:bg-transparent'>
						<Icons.Close className='size-5' />
					</Button>
				</SheetHeader>

				<ScrollArea className='h-full p-0 pb-28'>
					<form onSubmit={handleSubmit} className='flex flex-col gap-5'>
						<div className='space-y-1.5'>
							<Label className='text-xs text-878787 font-normal'>
								{c.titleLabel}
								<span aria-hidden className='ml-0.5 text-destructive'>*</span>
							</Label>
							<Input
								value={title}
								onChange={e => setTitle(e.target.value)}
								placeholder={c.titlePlaceholder.value}
								autoFocus
								autoComplete='off'
								aria-required
							/>
						</div>

						<div className='space-y-1.5'>
							<Label className='text-xs text-878787 font-normal'>
								{c.subjectLabel}
								<span aria-hidden className='ml-0.5 text-destructive'>*</span>
							</Label>
							<Input
								value={subjectName}
								onChange={e => setSubjectName(e.target.value)}
								placeholder={c.subjectPlaceholder.value}
								autoComplete='off'
								aria-required
							/>
						</div>

						<div className='space-y-1.5'>
							<Label className='text-xs text-878787 font-normal'>
								{c.phaseLabel}
								<span aria-hidden className='ml-0.5 text-destructive'>*</span>
							</Label>
							<Select value={phaseId} onValueChange={setPhaseId}>
								<SelectTrigger aria-required>
									<SelectValue placeholder={c.phasePlaceholder.value} />
								</SelectTrigger>
								<SelectContent>
									{phases.map(phase => (
										<SelectItem key={phase.id} value={phase.id}>
											{phase.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						<div className='space-y-1.5'>
							<Label className='text-xs text-878787 font-normal'>{c.parentLabel}</Label>
							<Select value={parentId} onValueChange={setParentId}>
								<SelectTrigger>
									<SelectValue placeholder={c.parentPlaceholder.value} />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value={NO_PARENT}>{c.parentNone}</SelectItem>
									{topLevelTasks.map(task => (
										<SelectItem key={task.id} value={task.id}>
											{task.title}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						{parentId !== NO_PARENT && (
							<div className='space-y-1.5'>
								<div className='flex items-center justify-between gap-3'>
									<Label className='text-xs text-878787 font-normal'>{c.lightweightLabel}</Label>
									<Switch checked={lightweight} onCheckedChange={setLightweight} />
								</div>
								<p className='text-xs text-606060'>{c.lightweightHint}</p>
							</div>
						)}

						<div className='space-y-1.5'>
							<Label className='text-xs text-878787 font-normal'>{c.externalIdLabel}</Label>
							<Input
								value={externalId}
								onChange={e => setExternalId(e.target.value)}
								placeholder={c.externalIdPlaceholder.value}
								autoComplete='off'
							/>
						</div>

						<div className='space-y-1.5'>
							<Label className='text-xs text-878787 font-normal'>{c.customerLabel}</Label>
							<Select value={customerId} onValueChange={setCustomerId}>
								<SelectTrigger>
									<SelectValue placeholder={c.customerPlaceholder.value} />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value={NO_CUSTOMER}>{c.customerNone}</SelectItem>
									{customers.map(cust => (
										<SelectItem key={cust.id} value={cust.id}>
											{cust.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						<div className='space-y-1.5'>
							<Label className='text-xs text-878787 font-normal'>{c.priorityLabel}</Label>
							<Select value={priority} onValueChange={setPriority}>
								<SelectTrigger>
									<SelectValue placeholder={c.priorityNone.value} />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value={NO_PRIORITY}>{c.priorityNone}</SelectItem>
									{PRIORITIES.map(p => (
										<SelectItem key={p} value={p}>
											{p}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						<div className='space-y-1.5'>
							<Label className='text-xs text-878787 font-normal'>{c.sizeLabel}</Label>
							<Select value={size} onValueChange={setSize}>
								<SelectTrigger>
									<SelectValue placeholder={c.sizeNone.value} />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value={NO_SIZE}>{c.sizeNone}</SelectItem>
									{SIZES.map(s => (
										<SelectItem key={s} value={s}>
											{s}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						<div className='space-y-1.5'>
							<Label className='text-xs text-878787 font-normal'>{c.labelsLabel}</Label>
							<Input
								value={labels}
								onChange={e => setLabels(e.target.value)}
								placeholder={c.labelsPlaceholder.value}
								autoComplete='off'
							/>
						</div>

						<div className='space-y-1.5'>
							<Label className='text-xs text-878787 font-normal'>{c.dueDateLabel}</Label>
							<Input type='date' value={dueDate} onChange={e => setDueDate(e.target.value)} autoComplete='off' />
						</div>

						<div className='space-y-1.5'>
							<Label className='text-xs text-878787 font-normal'>{c.notesLabel}</Label>
							<Textarea
								value={notes}
								onChange={e => setNotes(e.target.value)}
								placeholder={c.notesPlaceholder.value}
								rows={3}
								className='resize-none'
							/>
						</div>
					</form>
				</ScrollArea>

				<div className='absolute bottom-0 left-0 right-0 p-4 bg-background border-t border-border'>
					<div className='flex justify-end space-x-4'>
						<Button variant='outline' type='button' onClick={close}>
							{c.cancel}
						</Button>
						<SubmitButton type='button' isSubmitting={submitting} disabled={!canSubmit} onClick={submit}>
							{c.create}
						</SubmitButton>
					</div>
				</div>
			</SheetContent>
		</Sheet>
	)
}

export { WorkItemCreateSheet }
